import { useGetDocumentConnectionsQuery, useGetSecondaryConnectionsQuery } from '@api/utrv';
import { QUESTION, SMART_MATCH } from '@constants/terminology';
import { DashboardDivider } from '@g17eco/atoms/divider';
import { UniversalTrackerPlain } from '@g17eco/types/universalTracker';
import { useToggle } from '@hooks/useToggle';
import { SurveyModelMinimalUtrv } from '@g17eco/types/survey';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { UtrvConnection } from './types';
import { useConnectionContext } from './ConnectionContext';
import { useAppSelector } from '@reducers/index';
import { isStaff } from '@selectors/user';
import { getConnectionValue, getInputValue, getUtrvConnections, isUtrvDocumentConnection } from './utils';
import Eyes from '../../../images/eyes.gif';
import { getFormattedSurveyDate } from '@utils/survey';
import { ConnectionsGroup } from './ConnectionsGroup';
import React from 'react';
import { Loader } from '@g17eco/atoms/loader';
import { DocumentConnectionsGroup } from './DocumentConnectionsGroup';
import './styles.scss';
import { mapDocumentsToEvidenceFiles } from '@utils/evidence';
import { ReplaceAnswerModal } from './ReplaceAnswerModal';

const AddEvidenceConfirmModal = ({
  open,
  toggle,
  handleConfirm,
}: {
  open: boolean;
  toggle: () => void;
  handleConfirm: () => void;
}) => {
  return (
    <Modal isOpen={open} toggle={toggle} backdrop='static'>
      <ModalHeader toggle={toggle}>
        <span className='text-ThemeWarningExtraDark'>Add to Evidence section</span>
      </ModalHeader>
      <ModalBody>Would you like to add this file to the Evidence section?</ModalBody>
      <ModalFooter className='pt-0'>
        <Button color='link-secondary' onClick={toggle}>
          No - Don't Add
        </Button>
        <Button color='primary' onClick={handleConfirm}>
          Yes - Add
        </Button>
      </ModalFooter>
    </Modal>
  );
};

interface Props {
  utr: Pick<UniversalTrackerPlain, 'valueType' | 'targetDirection' | 'valueValidation' | 'unitType' | 'unit'>;
  utrv: SurveyModelMinimalUtrv;
}

export const Connections = ({ utr, utrv }: Props) => {
  const { data, isFetching } = useGetSecondaryConnectionsQuery({ utrvId: utrv._id });
  const { data: documentConnectionsData, isFetching: isFetchingDocumentConnections } = useGetDocumentConnectionsQuery({
    utrvId: utrv._id,
  });
  const isStaffUser = useAppSelector(isStaff);

  const {
    connection: selectedConnection,
    setConnection,
    setInputData,
    currentInputData,
    setFiles,
    files,
  } = useConnectionContext();
  const [showWarning, toggleShowWarning, setShowWarning] = useToggle(false);
  const [showAddEvidenceConfirm, toggleAddEvidenceConfirm, setAddEvidenceConfirm] = useToggle(false);

  if (isFetching || isFetchingDocumentConnections) {
    return <Loader />;
  }

  const { connections = [], utrs = [], surveys = [], integrationUtrs = [] } = data ?? {};
  const { connections: documentConnections = [], documents = [] } = documentConnectionsData ?? {};
  const { surveyIdToConnectionsMap, integratedConnections, allConnections } = getUtrvConnections(connections);

  if (!allConnections.length && !documentConnections.length) {
    return (
      <div className='d-flex flex-column align-items-center gap-3'>
        <img src={Eyes} alt={'no-connections'} width={120} />
        <p className='text-lg text-ThemeTextMedium text-center'>
          We looked, but this {QUESTION.SINGULAR} currently has no {SMART_MATCH.PLURAL}
        </p>
      </div>
    );
  }

  const handleClickConnection = (connection: UtrvConnection) => setConnection(connection);

  const handlePopulateInput = (connection: UtrvConnection) => {
    setConnection(connection);

    const currentValue = getInputValue({ utr, currentInputData, valueListCode: connection.valueListCode });
    const connectionValue = getConnectionValue({
      currentInputData,
      connection,
      utr,
      utrv,
    });

    if (currentValue === connectionValue) {
      return;
    }

    if (currentValue !== undefined) {
      setShowWarning(true);
      return;
    }

    setInputData(connectionValue);
    triggerAddToEvidenceModal(connection);
  };

  const handleConfirm = () => {
    if (!selectedConnection) {
      setInputData(undefined);
      setShowWarning(false);
      return;
    }

    const connectionValue = getConnectionValue({
      currentInputData,
      connection: selectedConnection,
      utr,
      utrv,
    });
    setInputData(connectionValue);
    setShowWarning(false);

    triggerAddToEvidenceModal(selectedConnection);
  };

  const triggerAddToEvidenceModal = (connection: UtrvConnection | undefined) => {
    if (
      !connection ||
      !isUtrvDocumentConnection(connection) ||
      files.some((file) => '_id' in file && file._id === connection.documentId)
    ) {
      return;
    }

    setAddEvidenceConfirm(true);
  };

  const handleAddToEvidence = () => {
    if (!selectedConnection || !isUtrvDocumentConnection(selectedConnection)) {
      return;
    }

    // TODO: Set reference too.
    const addedDocuments = mapDocumentsToEvidenceFiles(
      documents.filter((doc) => doc._id === selectedConnection.documentId),
    );
    setFiles(addedDocuments);
    setAddEvidenceConfirm(false);
  };

  return (
    <>
      <p className='text-sm text-ThemeTextLight'>
        Click <i className='fa-light fa-arrow-right-to-arc' /> to populate input field with selected answer
      </p>
      {integratedConnections.length ? (
        <ConnectionsGroup
          title='Emissions Tracker'
          connections={integratedConnections}
          integrationUtrs={integrationUtrs}
          utrs={[]}
          utr={utr}
          selectedConnection={selectedConnection}
          onClickConnection={handleClickConnection}
          onPopulateInput={handlePopulateInput}
          displayDetails={isStaffUser}
        />
      ) : null}
      {documentConnections.length ? (
        <DocumentConnectionsGroup
          connections={documentConnections}
          documents={documents}
          selectedConnection={selectedConnection}
          onClickConnection={handleClickConnection}
          onPopulateInput={handlePopulateInput}
          utr={utr}
          files={files}
          setFiles={setFiles}
        />
      ) : null}
      {surveys.map((survey, index) => {
        const connections = surveyIdToConnectionsMap.get(survey._id) ?? [];
        if (!connections.length) {
          return null;
        }

        return (
          <React.Fragment key={survey._id}>
            {index ? <DashboardDivider className='my-3' /> : null}
            <ConnectionsGroup
              title={getFormattedSurveyDate(survey)}
              connections={connections}
              integrationUtrs={[]}
              utrs={utrs}
              utr={utr}
              selectedConnection={selectedConnection}
              onClickConnection={handleClickConnection}
              onPopulateInput={handlePopulateInput}
              displayDetails={isStaffUser}
              initiativeId={survey.initiativeId}
              surveyScope={survey.scope}
            />
          </React.Fragment>
        );
      })}

      <ReplaceAnswerModal open={showWarning} toggle={toggleShowWarning} handleConfirm={handleConfirm} />
      <AddEvidenceConfirmModal
        open={showAddEvidenceConfirm}
        toggle={toggleAddEvidenceConfirm}
        handleConfirm={handleAddToEvidence}
      />
    </>
  );
};
